import Service, { service } from '@ember/service';
import CampaignService from './campaign-service';
import ContactModel from 'donor-elf-web/models/contact';
import CampaignModel from 'donor-elf-web/models/campaign';
import JournalInfo from 'donor-elf-web/lib/models/journal-info';
import JournalService from './journal-service';

interface ColumnMapping {
  column: string;
  property: string;
}

export interface HeaderInfo {
  maxPhonesLength: number;
  maxEmailsLength: number;
  regularColumnMapping: ColumnMapping[];
  customPropertyLabels: string[];
  groupNames: string[];
  journalInfos: JournalInfo[];
  campaigns: CampaignModel[];
}

export default class ContactsExportService extends Service {
  @service declare campaignService: CampaignService;
  @service declare journalService: JournalService;

  // used when no additional columns are needed
  // otherwise, use addContactsHeaderColumns() and addContactInfoToRow() below
  async perform(contacts: ContactModel[]) {
    let headerRow: string[] = [];
    let headerInfo = await this.addContactsHeaderColumns(headerRow, contacts);
    let rows: any[] = [headerRow];
    for (let contact of contacts) {
      let row: any[] = [];
      await this.addContactInfoToRow(row, contact, headerInfo);
      rows.push(row);
    }
    return rows;
  }

  /*
    addContactsHeaderColumns() and addContactInfoToRow() work together

    Example code:
    let rows = [['Date', 'Amount']];
    let headerInfo = this.contactsExport.addContactsHeaderColumns(rows[0], get(contactsCache, 'contacts'));
    for (let line of lineItems)
      let row = [line.transDate, line.amount];
      let contact = contactsCache.getById(line.contactId);
      this.contactsExport.addContactInfoToRow(row, contact, headerInfo);
      rows.push(row);
    });
  */
  async addContactsHeaderColumns(headerRow: any[], contacts: ContactModel[]) {
    await this.campaignService.load();
    await this.journalService.load();

    let i = 0;
    let headerInfo = this.getHeaderInfo(contacts);

    for (let x of headerInfo.regularColumnMapping) {
      headerRow.push(x.column);
    }
    for (i = 0; i < headerInfo.maxPhonesLength; i++) {
      headerRow.push(`Phone ${i + 1}`);
    }
    for (i = 0; i < headerInfo.maxEmailsLength; i++) {
      headerRow.push(`Email ${i + 1}`);
    }
    for (let label of headerInfo.customPropertyLabels) {
      headerRow.push(label);
    }
    for (let group of headerInfo.groupNames) {
      headerRow.push(`Group: ${group}`);
    }
    for (let journalInfo of headerInfo.journalInfos) {
      headerRow.push(`Journal: ${journalInfo.journal.label}`);
    }
    for (let campaign of headerInfo.campaigns) {
      headerRow.push(`Campaign: ${campaign.label}`);
    }

    return headerInfo;
  }

  // making this async just in case we need it later then we don't have to
  // update the calling code
  async addContactInfoToRow(
    row: any[],
    contact: ContactModel | any,
    headerInfo: HeaderInfo,
  ) {
    let i = 0;
    if (contact == null) {
      contact = {};
    }
    for (let x of headerInfo.regularColumnMapping) {
      row.push(contact[x.property]);
    }
    let phonesData = contact.phones?.data || [];
    for (i = 0; i < headerInfo.maxPhonesLength; i++) {
      if (phonesData.length > i) {
        row.push(phonesData[i]?.['v']);
      } else {
        row.push(null);
      }
    }
    let emailsData = contact.emails?.data || [];
    for (i = 0; i < headerInfo.maxEmailsLength; i++) {
      if (emailsData.length > i) {
        row.push(emailsData[i]?.['v']);
      } else {
        row.push(null);
      }
    }
    for (let label of headerInfo.customPropertyLabels) {
      let prop = contact.customPropertiesByLabel?.[label];
      if (prop) {
        if (prop.t === 'l') {
          row.push(Object.keys(prop.v).join(' || '));
        } else {
          row.push(prop.v);
        }
      } else {
        row.push(null);
      }
    }
    for (let group of headerInfo.groupNames) {
      if ((contact.groups || []).indexOf(group) >= 0) {
        row.push('X');
      } else {
        row.push(null);
      }
    }
    if (headerInfo.journalInfos.length > 0) {
      let contactJournalInfos =
        this.journalService.journalInfosByContactId[contact.id] || [];
      for (let journalInfo of headerInfo.journalInfos) {
        if (contactJournalInfos.includes(journalInfo)) {
          row.push('X');
        } else {
          row.push(null);
        }
      }
    }
    if (headerInfo.campaigns.length > 0) {
      let campaignContacts =
        this.campaignService.campaignContactsByContactId[contact.id] || [];
      for (let campaign of headerInfo.campaigns) {
        if (campaignContacts.some((c) => c.campaignId === campaign.id)) {
          row.push('X');
        } else {
          row.push(null);
        }
      }
    }
  }

  getHeaderInfo(contacts: ContactModel[]): HeaderInfo {
    let maxPhonesLength = 0;
    let maxEmailsLength = 0;
    let customPropertyMapping: { [label: string]: true } = {};
    let groupMapping: { [group: string]: true } = {};
    let journalInfos = this.journalService.sortedJournalInfos;
    let campaigns: CampaignModel[] = this.campaignService.campaignsEnabled
      ? this.campaignService.sortedCampaigns
      : [];

    for (let contact of contacts) {
      let phonesLength = contact.phones?.data?.length || 0;
      if (phonesLength > maxPhonesLength) {
        maxPhonesLength = phonesLength;
      }
      let emailsLength = contact.emails?.data?.length || 0;
      if (emailsLength > maxEmailsLength) {
        maxEmailsLength = emailsLength;
      }
      for (let prop of contact.custom?.properties || []) {
        customPropertyMapping[prop.l] = true;
      }
      for (let group of contact.groups || []) {
        groupMapping[group] = true;
      }
    }

    return {
      maxPhonesLength,
      maxEmailsLength,
      regularColumnMapping,
      customPropertyLabels: Object.keys(customPropertyMapping).sort(),
      groupNames: Object.keys(groupMapping).sort(),
      journalInfos,
      campaigns,
    };
  }
}

const regularColumnMapping: ColumnMapping[] = [
  {
    column: 'Display Name',
    property: 'name',
  },
  {
    column: 'Mailing Name',
    property: 'mailingName',
  },
  {
    column: 'Greeting',
    property: 'greeting',
  },
  {
    column: 'First Name',
    property: 'firstName',
  },
  {
    column: 'Last Name',
    property: 'lastName',
  },
  {
    column: 'Spouse First Name',
    property: 'spouseFirstName',
  },
  {
    column: 'Spouse Last Name',
    property: 'spouseLastName',
  },
  {
    column: 'Address Line 1',
    property: 'address1',
  },
  {
    column: 'Address Line 2',
    property: 'address2',
  },
  {
    column: 'City',
    property: 'city',
  },
  {
    column: 'State',
    property: 'state',
  },
  {
    column: 'Zip',
    property: 'zip',
  },
  {
    column: 'Country',
    property: 'country',
  },
  {
    column: 'Deceased',
    property: 'deceased',
  },
  {
    column: 'Spouse is Deceased',
    property: 'spouseDeceased',
  },
  {
    column: 'Hidden',
    property: 'hidden',
  },
];
